import React from 'react';
import Icon from '../../../components/AppIcon';

const getLevelStatus = (level) => {
  if (level > 90) return { label: 'Clipping', tone: 'red', detail: 'Lower input gain' };
  if (level > 55) return { label: 'Ready', tone: 'green', detail: 'Healthy signal' };
  if (level > 20) return { label: 'Usable', tone: 'lime', detail: 'Can record' };
  if (level > 0) return { label: 'Low', tone: 'yellow', detail: 'Raise mic gain' };
  return { label: 'Silent', tone: 'gray', detail: 'Speak into mic' };
};

const toneClasses = {
  green: 'border-green-500/40 bg-green-950/20 text-green-300',
  lime: 'border-lime-500/40 bg-lime-950/20 text-lime-300',
  yellow: 'border-yellow-500/40 bg-yellow-950/20 text-yellow-300',
  red: 'border-red-500/40 bg-red-950/20 text-red-300',
  cyan: 'border-cyan-500/40 bg-cyan-950/20 text-cyan-300',
  gray: 'border-gray-700 bg-gray-900 text-gray-300',
};

const CheckItem = ({ ok, label, detail }) => (
  <div className={`flex items-center gap-2 rounded border px-2 py-1.5 ${ok ? toneClasses.green : toneClasses.gray}`}>
    <Icon name={ok ? 'CheckCircle2' : 'Circle'} size={13} />
    <div className="min-w-0">
      <div className="truncate text-[10px] font-mono font-bold uppercase">{label}</div>
      <div className="truncate text-[9px] opacity-70">{detail}</div>
    </div>
  </div>
);

const MicReadinessPanel = ({
  selectedDevice,
  beatLoaded,
  armedChannel,
  inputLevel = 0,
  monitoringEnabled,
  manualLatencyMs = 0,
  onLatencyChange,
  disabled,
}) => {
  const levelStatus = getLevelStatus(inputLevel);
  const readyCount = [
    Boolean(selectedDevice),
    Boolean(beatLoaded),
    Boolean(armedChannel),
    inputLevel > 20 && inputLevel < 90,
  ].filter(Boolean).length;
  const readyToRecord = readyCount >= 4;
  const presets = [
    { label: '0 ms', value: 0, title: 'Direct wired monitor' },
    { label: '60 ms', value: 60, title: 'USB/interface drift' },
    { label: '150 ms', value: 150, title: 'Bluetooth headphones' },
    { label: '250 ms', value: 250, title: 'Heavy wireless delay' },
  ];

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-950/70 p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className={`flex h-9 w-9 items-center justify-center rounded border ${readyToRecord ? toneClasses.green : toneClasses.cyan}`}>
            <Icon name={readyToRecord ? 'BadgeCheck' : 'MicVocal'} size={17} />
          </div>
          <div>
            <h4 className="text-sm font-medium">Mic Readiness</h4>
            <p className="text-[10px] text-muted-foreground">{readyToRecord ? 'Ready to record a take' : `${readyCount}/4 checks ready`}</p>
          </div>
        </div>
        <span className={`rounded border px-2 py-1 text-[10px] font-mono uppercase ${toneClasses[levelStatus.tone]}`}>
          {levelStatus.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        <CheckItem ok={Boolean(selectedDevice)} label="Device" detail={selectedDevice ? 'Input selected' : 'Select mic'} />
        <CheckItem ok={Boolean(beatLoaded)} label="Beat" detail={beatLoaded ? 'Loaded' : 'Import beat'} />
        <CheckItem ok={Boolean(armedChannel)} label="Arm" detail={armedChannel || 'Choose track'} />
        <CheckItem ok={inputLevel > 20 && inputLevel < 90} label="Level" detail={levelStatus.detail} />
      </div>

      <div className="rounded border border-gray-800 bg-black/30 p-2">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-mono uppercase text-gray-400">Latency Preset</div>
            <div className="text-[9px] text-gray-500">
              {monitoringEnabled ? 'Use headphones to avoid feedback' : 'Adjust if recorded takes land late'}
            </div>
          </div>
          <span className="rounded border border-cyan-500/40 px-2 py-0.5 text-[10px] font-mono text-cyan-200">
            {manualLatencyMs} ms
          </span>
        </div>
        <div className="grid grid-cols-4 gap-1">
          {presets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              title={preset.title}
              onClick={() => onLatencyChange?.(preset.value)}
              disabled={disabled || manualLatencyMs === preset.value}
              className={`rounded border px-1.5 py-1.5 text-[10px] font-mono transition ${
                manualLatencyMs === preset.value
                  ? 'border-cyan-400 bg-cyan-900/40 text-cyan-100'
                  : 'border-gray-700 bg-gray-900 text-gray-300 hover:border-cyan-500/60 hover:text-cyan-200'
              } disabled:opacity-50`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MicReadinessPanel;
